import { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useSession } from "@clerk/clerk-react";
import { Button } from "@heroui/button";
import { useWakeLock } from "react-screen-wake-lock";
import Orb from "../react-bits/orb/orb";
import { SETTINGS } from "../../utils/settings";
import { getCurrentDateTime, isTimePassed } from "@/utils/dates";
import {
    getByShortCode as getByShortCodeApi,
    setMeetsStartTime as setMeetsStartTimeApi,
} from "@/api/meets";
import { metrics, startSession, recordSectionStart, recordSectionEnd, summarize } from "@/utils/metrics";

const API_URL = `${SETTINGS["API_ENDPOINT"]}/audio/api`;
const SECTION_MS = 10_000;

const isSafari = () =>
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const extFromMime = (mime) => {
  if (!mime) return ".webm";
  const base = mime.split(";")[0].toLowerCase();
  if (base.includes("wav")) return ".wav";
  if (base.includes("mp4")) return ".m4a";
  if (base.includes("ogg")) return ".ogg";
  if (base.includes("webm")) return ".webm";
  return ".webm";
};

function floatTo16BitPCM(float32Array) {
  const output = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    let s = Math.max(-1, Math.min(1, float32Array[i]));
    output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return output;
}

function writeString(view, offset, string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

function encodeWAV(float32Data, sampleRate) {
  const channelCount = 1;
  const pcm16 = floatTo16BitPCM(float32Data);
  const bytesPerSample = 2;
  const blockAlign = channelCount * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = pcm16.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  writeString(view, 22, "\x01\x00"); // channels=1
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeString(view, 36, "data");
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < pcm16.length; i++, offset += 2) {
    view.setInt16(offset, pcm16[i], true);
  }
  return new Blob([view], { type: "audio/wav" });
}

function resampleLinear(float32, fromRate, toRate) {
  if (fromRate === toRate) return float32;
  const ratio = toRate / fromRate;
  const newLen = Math.max(1, Math.round(float32.length * ratio));
  const out = new Float32Array(newLen);
  const step = (float32.length - 1) / (newLen - 1);
  for (let i = 0; i < newLen; i++) {
    const pos = i * step;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, float32.length - 1);
    const t = pos - i0;
    out[i] = float32[i0] * (1 - t) + float32[i1] * t;
  }
  return out;
}

const ActiveMeetsComp = ({ _short_code }) => {
const { session } = useSession();

const location = useLocation();
const navigate = useNavigate();

const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [status, setStatus] = useState("Idle");
  const [recordedTime, setRecordedTime] = useState(0);
  const mediaRecorderRef = useRef(null); // non-Safari
  const micStreamRef = useRef(null);     // holds the live mic stream
  const sectionIndexRef = useRef(0);
  const timerRef = useRef(null);
  const [stitchingAudio, setStitchingAudio] = useState(false);
  const [meeting, setMeeting] = useState(null);
  const [meetingStarted, setMeetingStarted] = useState(false);

  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);
  const procRef = useRef(null);
  const wavSampleRateRef = useRef(0);
  const wavBufferRef = useRef(new Float32Array(0));
  const wavSamplesPerSectionRef = useRef(0);
  const safariPausedRef = useRef(false);

  const hasSetStartTimeRef = useRef(false);

  const { isSupported, request, release } = useWakeLock();

  useEffect(() => {
    getMeetingRec();
    if (!navigator.mediaDevices?.getUserMedia) {
      alert("Your browser does not support audio recording");
    }
    return () => {
      try {
        clearInterval(timerRef.current);
        stopSafariWavIfRunning();
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== "inactive"
        ) {
          mediaRecorderRef.current.stop();
        }
        // Ensure mic is fully released on unmount
        micStreamRef.current?.getTracks?.().forEach((t) => t.stop());
        micStreamRef.current = null;
      } catch {}
      releaseWakeLock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && recording) {
        setTimeout(acquireWakeLock, 50);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [recording, isSupported]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (recording) {
        event.preventDefault();
        event.returnValue = "";
        return "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [recording]);

  async function getMeetingRec() {
    try {
      const token = await session.getToken();
      const _resp = await getByShortCodeApi(_short_code, token);
      if (_resp.data.Result === "OK") {
        const record = _resp.data.Record;
        const real_start_time = record?.start_time?.real
        const real_end_time = record?.end_time?.real
        const passed = isTimePassed(real_end_time)
        if(passed){
          navigate(`/meetings/view/${_short_code}`)
        }
        setMeeting(record);
        const isStarted = real_start_time && real_start_time !== "";
        setMeetingStarted(Boolean(isStarted));
        hasSetStartTimeRef.current = Boolean(isStarted);
      } else {
        setMeeting(null);
      }
    } catch (e) {
      console.error(e);
      setMeeting(null);
    }
  }

  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const pickMimeType = () => {
    const candidates = ["audio/webm;codecs=opus", "audio/ogg", "audio/mp4"];
    for (const type of candidates) {
      if (MediaRecorder.isTypeSupported?.(type)) return type;
    }
    return "";
  };

  const uploadSection = async (blob, sectionIndex) => {
    recordSectionStart(sectionIndex, blob.size);

    const ext = extFromMime(blob.type);
// Note: The backend ignores this numeric filename and uses the timestamp from 'date_time'.
    const filename = `section-${sectionIndex
      .toString()
      .padStart(6, "0")}${ext}`;
    const typedBlob = new Blob([blob], { type: blob.type || "audio/webm" });
    const current_date_time = getCurrentDateTime();

    const formData = new FormData();
    formData.append("file", typedBlob, filename);
    formData.append("meeting_id", meeting?._id || "");
// Keep section_index for debugging, but back-end relies on date_time
    formData.append("section_index", sectionIndex);
    formData.append("blob_mime", typedBlob.type || "");
    formData.append("date_time", current_date_time);

    try {
      const token = await session.getToken();
      const res = await axios.post(`${API_URL}/upload-section`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          TOKEN: token,
        },
      });
      recordSectionEnd(sectionIndex, true, res.status);
    } catch (err) {
      recordSectionEnd(
        sectionIndex,
        false,
        err?.response?.status || "ERR_UPLOAD"
      );
      console.error("Upload section failed:", err);
    }
  };

  const acquireWakeLock = async () => {
    if (!isSupported) return;
    try {
      await request();
    } catch (e) {
      console.warn("WakeLock request failed:", e);
    }
  };

  const releaseWakeLock = async () => {
    if (!isSupported) return;
    try {
      await release();
    } catch (e) {
      console.warn("WakeLock release failed:", e);
    }
  };

  // Always create a new mic stream when starting.
  async function getFreshMicStream() {
    const constraints = {
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        channelCount: 1,
      },
    };
    return await navigator.mediaDevices.getUserMedia(constraints);
  }

  // Requests mic access each time (prompts if needed).
  async function ensureMicPermission() {
    const stream = await getFreshMicStream();
    return stream; // reuse for the session
  }

  const startMediaRecorder = async (providedStream) => {
    const stream = providedStream || (await ensureMicPermission());
    micStreamRef.current = stream;

    const mimeType = pickMimeType();
    const opts = mimeType
      ? { mimeType, audioBitsPerSecond: 24000 }
      : { audioBitsPerSecond: 24000 };

    const recorder = new MediaRecorder(stream, opts);

    // Reset state only if we are starting a *new* recording (not a resume)
    if (!recording && !paused) {
      sectionIndexRef.current = 0;
      setRecordedTime(0);
    }

    recorder.ondataavailable = async (event) => {
      if (event.data?.size > 0) {
        const idx = sectionIndexRef.current++;
        await uploadSection(event.data, idx);
      }
    };

    recorder.onstop = async () => {
      clearInterval(timerRef.current);
      await releaseWakeLock();
      await finalizeSession(micStreamRef.current);
    };

    recorder.start(SECTION_MS);
    mediaRecorderRef.current = recorder;

    startTicking();
    setStatus("Recording");
    setRecording(true);
    setPaused(false);
  };

  const startSafariWav = async (providedStream) => {
    const stream = providedStream || (await ensureMicPermission());
    micStreamRef.current = stream;

    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const source = audioCtx.createMediaStreamSource(stream);
    const processor = audioCtx.createScriptProcessor(4096, 1, 1);

    wavSampleRateRef.current = audioCtx.sampleRate;
    
// Reset state only if we are starting a *new* recording (not a resume)
if (!recording && !paused) {
      wavBufferRef.current = new Float32Array(0);
      sectionIndexRef.current = 0;
      setRecordedTime(0);
    }

    wavSamplesPerSectionRef.current = Math.floor(
      (wavSampleRateRef.current * SECTION_MS) / 1000
    );
    safariPausedRef.current = false;

    processor.onaudioprocess = async (e) => {
      if (safariPausedRef.current) return;
      const input = e.inputBuffer.getChannelData(0);
      const prev = wavBufferRef.current;
      const merged = new Float32Array(prev.length + input.length);
      merged.set(prev, 0);
      merged.set(input, prev.length);
      wavBufferRef.current = merged;

      while (wavBufferRef.current.length >= wavSamplesPerSectionRef.current) {
        const section = wavBufferRef.current.slice(
          0,
          wavSamplesPerSectionRef.current
        );
        wavBufferRef.current = wavBufferRef.current.slice(
          wavSamplesPerSectionRef.current
        );

        const TARGET_SR = 16000;
        const section16k = resampleLinear(
          section,
          wavSampleRateRef.current,
          TARGET_SR
        );
        const wavBlob = encodeWAV(section16k, TARGET_SR);

        const idx = sectionIndexRef.current++;
        await uploadSection(new Blob([wavBlob], { type: "audio/wav" }), idx);
      }
    };

    source.connect(processor);
    processor.connect(audioCtx.destination);

    audioCtxRef.current = audioCtx;
    sourceRef.current = source;
    procRef.current = processor;

    startTicking();
    setStatus("Recording");
    setRecording(true);
    setPaused(false);
  };

  const stopSafariWavIfRunning = () => {
    try {
      if (procRef.current) procRef.current.disconnect();
      if (sourceRef.current) sourceRef.current.disconnect();
      if (audioCtxRef.current) audioCtxRef.current.close();
    } catch {}
    procRef.current = null;
    sourceRef.current = null;
    audioCtxRef.current = null;
  };

  const startTicking = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setRecordedTime((prev) => prev + 1);
    }, 1000);
  };
  const stopTicking = () => {
    clearInterval(timerRef.current);
  };

  const finalizeSession = async (stream) => {
    setStitchingAudio(true);
    try {
      const token = await session.getToken();
      const res = await axios.post(
        `${API_URL}/complete-recording`,
        {
          short_code: _short_code,
          session_id: metrics.sessionId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            TOKEN: token,
          },
        }
      );
      console.log("Recording complete:", res.data);
      navigate(`/meetings/view/${_short_code}`);
    } catch (e) {
      console.error("Finalize failed:", e);
    }
    setStitchingAudio(false);

    console.log("Recording metrics summary:", summarize());

    setRecording(false);
    setPaused(false);
    setStatus("Idle");

    try {
      stream?.getTracks?.().forEach((t) => t.stop());
      micStreamRef.current = null;
    } catch {}
  };

  const startRecording = async () => {
    try {
      // Always request mic access on each start (prompts when needed)
      const freshStream = await ensureMicPermission();

      await acquireWakeLock();
      startSession();

      if (!meetingStarted && !hasSetStartTimeRef.current) {
        await setMeetingStartTime();
      }

      if (isSafari()) {
        await startSafariWav(freshStream);
      } else {
        await startMediaRecorder(freshStream);
      }
    } catch (err) {
      console.error("Recording error:", err);
      alert("Recording failed: " + err.message);
      await releaseWakeLock();
      try {
        micStreamRef.current?.getTracks?.().forEach((t) => t.stop());
        micStreamRef.current = null;
      } catch {}
    }
  };

  async function setMeetingStartTime() {
    try {
      const token = await session.getToken();
      const _resp = await setMeetsStartTimeApi(meeting["_id"], token);
      if (_resp?.data?.Result === "SUCCESS") {
        setMeetingStarted(true);
        hasSetStartTimeRef.current = true;
      }
    } catch (err) {
      console.error("setMeetingStartTime failed:", err);
    }
  }

  const pauseRecording = async () => {
    try {
      if (!recording || paused) return;
      if (isSafari()) {
        safariPausedRef.current = true;
      } else {
        mediaRecorderRef.current?.pause?.();
      }
      setPaused(true);
      stopTicking();
      setStatus("Paused");
    } catch (e) {
      console.error("Pause failed:", e);
    }
  };

  const resumeRecording = async () => {
    try {
      if (!recording || !paused) return;
      await acquireWakeLock();

// **CRITICAL FIX**: When resuming after backgrounding (app close), 
// the underlying browser audio stream is often dead. We must stop the old stream/context 
// and fully restart the recording flow to get a fresh, live audio stream.

      if (isSafari()) {
// Safari (Web Audio API)
// 1. Clean up old state
stopSafariWavIfRunning(); // Disconnects and closes AudioContext
micStreamRef.current?.getTracks?.().forEach((t) => t.stop()); // Kills old stream
micStreamRef.current = null;
// 2. Restart the entire flow (gets a new stream and context)
await startSafariWav(null); 
      } else {
// MediaRecorder (Non-Safari)
if (mediaRecorderRef.current?.state === "paused") {
// Try standard resume if the state is still 'paused'
              mediaRecorderRef.current.resume();
} else {
// Recorder is likely corrupted/dead (state is 'inactive' or otherwise broken). 
// 1. Clean up old state
mediaRecorderRef.current = null;
micStreamRef.current?.getTracks?.().forEach((t) => t.stop());
micStreamRef.current = null;
// 2. Restart the entire flow (gets a new stream and recorder)
await startMediaRecorder(null);
}
      }
      setPaused(false);
      startTicking();
      setStatus("Recording");
    } catch (e) {
      console.error("Resume failed, attempting full restart:", e);
// Fallback: If resume or forced restart fails, try the full start path.
await startRecording(); 
    }
  };

  const finishRecording = async () => {
    try {
      if (!recording) return;

      if (isSafari()) {
        stopTicking();
        stopSafariWavIfRunning();

        const remaining = wavBufferRef.current;
        if (remaining && remaining.length > 2000) {
          const TARGET_SR = 16000;
          const rem16k = resampleLinear(
            remaining,
            wavSampleRateRef.current,
            TARGET_SR
          );
          const wavBlob = encodeWAV(rem16k, TARGET_SR);
          const idx = sectionIndexRef.current++;
          await uploadSection(new Blob([wavBlob], { type: "audio/wav" }), idx);
        }

        await releaseWakeLock();
        await finalizeSession(micStreamRef.current || new MediaStream());
      } else {
        if (mediaRecorderRef.current?.state === "paused") {
          try {
            mediaRecorderRef.current.resume();
          } catch {}
        }
        mediaRecorderRef.current?.stop();
        stopTicking();
        await releaseWakeLock();
      }
    } catch (e) {
      console.error("Finish failed:", e);
    }
  };

  useEffect(() => {
    if (recording && !meetingStarted && meeting && !hasSetStartTimeRef.current) {
      setMeetingStartTime();
    }
  }, [recording, meetingStarted, meeting]);

  const safeDescription = useMemo(() => {
    const text = meeting?.description || "";
    return text.length > 200 ? text.substring(0, 200) + "..." : text;
  }, [meeting]);

  const isLoading = meeting === null;

  return (
    <>
      <div className="flex flex-col gap-2 p-4">
        <h1 className="text-2xl font-bold leading-7 capitalize">
          {isLoading ? "Loading..." : meeting.title}
        </h1>
        <p className="text-sm text-neutral-600 dark:text-zinc-300 first-letter:uppercase">
          {isLoading ? "Preparing meeting..." : safeDescription}
        </p>
        {!isLoading && (
          <div className="text-xs opacity-70">
            Status: <span className="capitalize">{status}</span>{" "}
            {meetingStarted ? "• Meeting started" : "• Not started"}
        </div>
        )}
      </div>

      <div style={{ width: "100%", height: "500px", position: "relative" }}>
        <Orb
          hoverIntensity={0.5}
          rotateOnHover={true}
          hue={0}
          forceHoverState={!isLoading && recording && !paused}
        />
      </div>

      {/* Controls */}
      <div className="fixed left-1/2 -translate-x-1/2 bottom-[100px] z-[60] w-[min(92vw,600px)]">
        <div className="flex items-center gap-2 rounded-2xl p-2 border border-white/10 justify-center">
          {/* Timer */}
          <div className="px-3 py-1 rounded-xl border border-white/10 bg-white/5">
            <span className="tabular-nums">{formatTime(recordedTime)}</span>
          </div>

          {!isLoading && !recording && (
            <Button
              className="flex-1"
              color="primary"
              onPress={startRecording}
              isDisabled={stitchingAudio}
            >
              {meetingStarted ? "Continue Recording" : "Start Recording"}
            </Button>
          )}

          {!isLoading && recording && !paused && (
            <>
              <Button
                className="flex-1"
                variant="flat"
                onPress={pauseRecording}
                isDisabled={stitchingAudio}
              >
                Pause
              </Button>
              <Button
                className="flex-1"
                color="primary"
                onPress={finishRecording}
                isDisabled={stitchingAudio}
              >
                Finish
              </Button>
            </>
          )}

          {!isLoading && recording && paused && (
            <>
              <Button
                className="flex-1"
                color="primary"
                onPress={resumeRecording}
                isDisabled={stitchingAudio}
              >
                Continue
              </Button>
              <Button
                className="flex-1"
                variant="flat"
                onPress={finishRecording}
                isDisabled={stitchingAudio}
              >
                Finish
              </Button>
            </>
          )}
        </div>
      </div>
</>
);
};

export default ActiveMeetsComp;